import { formatDanishLongDate } from "@/util/date";
import { LegalAidEvidenceListModel } from "../models/legalAid";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import ReportedStatusBadge from "./ReportedStatusBadge";
import { formatLessons } from "../utils/lessonLimits";

type LegalAidTableProps = {
  isError?: boolean;
  isLoading?: boolean;
  onLegalAidClick?: (legalAidId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  legalAids: LegalAidEvidenceListModel[];
  showStatusColumn?: boolean;
};

const LegalAidTable = ({
  isError = false,
  isLoading = false,
  onLegalAidClick,
  pagination,
  legalAids,
  showStatusColumn = true,
}: LegalAidTableProps) => {
  const columns: CleTableColumn<LegalAidEvidenceListModel>[] = [
    {
      id: "fileName",
      header: "Dokument",
      title: (legalAid) => legalAid.fileName ?? "",
      cell: (legalAid) => legalAid.fileName ?? "-",
    },
    {
      id: "createdAt",
      header: "Oprettet",
      cell: (legalAid) => formatDanishLongDate(legalAid.createdAt),
    },
    {
      id: "lessons",
      header: "Lektioner",
      className: "text-end",
      cell: (legalAid) => formatLessons(legalAid.numberOfLessons),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (legalAid: LegalAidEvidenceListModel) => (
              <ReportedStatusBadge reported={legalAid.isReported} />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        data={legalAids}
        emptyDescription="Når du opretter dokumentation for advokatvagt eller retshjælp, vises den her."
        emptyTitle="Ingen dokumentation oprettet endnu"
        errorMessage="Advokatvagt/retshjælp kunne ikke indlæses lige nu."
        isError={isError}
        isLoading={isLoading}
        getRowId={(legalAid) => legalAid.id}
        rowAction={
          onLegalAidClick
            ? {
                onClick: (id) => onLegalAidClick(id),
              }
            : undefined
        }
      />
      {pagination != null && legalAids.length > 0 ? (
        <ClePagination
          ariaLabel="Advokatvagt/retshjælp pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="dokumenter"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default LegalAidTable;
